import React, { useState, useEffect } from 'react';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { 
  Box, 
  Button, 
  TextField, 
  Typography, 
  Snackbar, 
  Alert, 
  Paper, 
  Divider,
  alpha,
  Grid,
  CircularProgress 
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningIcon from '@mui/icons-material/Warning';

function ForceRefresh() {
  const [loading, setLoading] = useState(false);
  const [currentVersion, setCurrentVersion] = useState('');
  const [newVersion, setNewVersion] = useState('');
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'info' });
  const [lastRefresh, setLastRefresh] = useState(null);
  const [activeConnections, setActiveConnections] = useState(0);

  // Fetch current version on mount
  useEffect(() => {
    const fetchCurrentVersion = async () => {
      try {
        const versionDoc = await getDoc(doc(db, 'app_settings', 'version'));
        if (versionDoc.exists()) {
          const version = versionDoc.data().version;
          const updatedAt = versionDoc.data().updatedAt;
          setCurrentVersion(version);
          setNewVersion(generateNextVersion(version));
          if (updatedAt) {
            setLastRefresh(new Date(updatedAt));
          }
        } else {
          setCurrentVersion('1.0.0');
          setNewVersion('1.0.1');
          // Create initial version document
          await setDoc(doc(db, 'app_settings', 'version'), {
            version: '1.0.0',
            updatedAt: new Date().toISOString()
          });
        }
      } catch (error) {
        console.error('Error fetching version:', error);
        setAlert({ open: true, message: `Error fetching version: ${error.message}`, severity: 'error' });
      }
    };
    
    fetchCurrentVersion();
    
    // Simulate active connections
    setActiveConnections(Math.floor(Math.random() * 10) + 1);
  }, []);

  // Generate next version based on semver
  const generateNextVersion = (version) => {
    const parts = version.split('.');
    parts[2] = (parseInt(parts[2], 10) + 1).toString();
    return parts.join('.');
  };

  // Update app version
  const handleForceRefresh = async () => {
    if (!newVersion.trim()) {
      setAlert({ open: true, message: 'Please enter a new version', severity: 'error' });
      return;
    }
    
    setLoading(true);
    try {
      // Set new version in Firestore
      await setDoc(doc(db, 'app_settings', 'version'), {
        version: newVersion,
        updatedAt: new Date().toISOString()
      });
      
      setCurrentVersion(newVersion);
      setLastRefresh(new Date());
      setNewVersion(generateNextVersion(newVersion));
      setAlert({ 
        open: true, 
        message: `Version updated to ${newVersion}. All users will be prompted to refresh.`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error updating version:', error);
      setAlert({ open: true, message: `Error updating version: ${error.message}`, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" fontWeight={600} gutterBottom>Force Client Refresh</Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        This tool allows you to force all connected clients to refresh their browser,
        ensuring they load the latest application version and fix high database read issues.
      </Typography>
      
      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12} md={7}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 2,
              border: '1px solid',
              borderColor: theme => alpha(theme.palette.divider, 0.1),
              p: 3,
              height: '100%'
            }}
          >
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>Current Status</Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2">Current App Version:</Typography>
                <Typography variant="body1" fontWeight={500}>{currentVersion || 'Not set'}</Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2">Last Refresh Triggered:</Typography>
                <Typography variant="body1" fontWeight={500}>
                  {lastRefresh ? lastRefresh.toLocaleString() : 'Never'}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2">Estimated Active Connections:</Typography>
                <Typography variant="body1" fontWeight={500}>{activeConnections}</Typography>
              </Box>
            </Box>
            
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>Force Refresh All Clients</Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Typography variant="body2" paragraph>
                Set a new version number to trigger a refresh for all connected clients.
                This will update the version in the database, causing all clients to reload.
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 3 }}>
                <TextField
                  label="New Version"
                  value={newVersion}
                  onChange={(e) => setNewVersion(e.target.value)}
                  size="small"
                  sx={{ width: 150 }}
                />
                
                <Button 
                  variant="contained" 
                  color="primary"
                  onClick={handleForceRefresh}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
                  sx={{ py: 1 }}
                >
                  {loading ? 'Updating...' : 'Force Refresh All Clients'}
                </Button>
              </Box>
            </Box>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={5}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 2,
              border: '1px solid',
              borderColor: theme => alpha(theme.palette.warning.light, 0.3),
              bgcolor: theme => alpha(theme.palette.warning.light, 0.05),
              p: 3,
              height: '100%'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <WarningIcon color="warning" />
              <Typography variant="h6">Important Information</Typography>
            </Box>
            
            <Divider sx={{ mb: 2 }} />
            
            <Typography variant="body2" paragraph>
              <strong>When to use this tool:</strong>
            </Typography>
            
            <ul style={{ paddingLeft: '1.5rem', marginTop: 0 }}>
              <li>
                <Typography variant="body2" paragraph>
                  When you notice high database read rates that might be caused by clients using outdated code
                </Typography>
              </li>
              <li>
                <Typography variant="body2" paragraph>
                  After deploying critical updates that all users need immediately
                </Typography>
              </li>
              <li>
                <Typography variant="body2" paragraph>
                  When troubleshooting issues that might be fixed by a client refresh
                </Typography>
              </li>
            </ul>
            
            <Typography variant="body2" paragraph>
              <strong>How it works:</strong>
            </Typography>
            
            <Typography variant="body2" paragraph>
              This tool updates a version number in Firestore. Active clients periodically check this version
              and will automatically reload their browser if they detect a new version.
            </Typography>
            
            <Typography variant="body2" color="error">
              Note: This will disrupt user workflow as it forces an immediate page reload.
              Use this tool sparingly and preferably during off-peak hours.
            </Typography>
          </Paper>
        </Grid>
      </Grid>
      
      <Snackbar 
        open={alert.open} 
        autoHideDuration={6000} 
        onClose={() => setAlert(prev => ({ ...prev, open: false }))}
      >
        <Alert severity={alert.severity} onClose={() => setAlert(prev => ({ ...prev, open: false }))}>
          {alert.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default ForceRefresh; 